import React, { useMemo } from 'react'
import { Translation } from 'react-i18next'
import { Button, IconButton, withStyles } from '@material-ui/core'
import {
  Form,
  Field,
  useForm,
  useFieldArray,
  useWatch,
  WatchField
} from '../../../form'
import classNames from 'classnames'
import RichTextEditorComponent from '../lightEntrepreneurs/richTextEditor.component'
import BasicDateCalendar from '../../../utils/DateCalendar.utils'
import { Controller } from 'react-hook-form'
import Table from '@material-ui/core/Table'
import TableBody from '@material-ui/core/TableBody'
import TableCell from '@material-ui/core/TableCell'
import TableHead from '@material-ui/core/TableHead'
import TableRow from '@material-ui/core/TableRow'
import ShiftDateRowComponent from './shiftDateRow.component'
import LightEntreSelectField from '../common/lightEntreSelection.field'
import { workShiftFormValidate } from '../../../containers/employer/validate'
import moment from 'moment'
import SumField from '../../invoice/sumField.component'
import { calculateSum } from '../../invoice/invoiceInputTable.component'
import { AddCircle } from '@material-ui/icons'

const styles = (theme) => ({
  root: {
    width: '100%',
    overflowX: 'auto',
    '&::-webkit-scrollbar': {
      width: '12px !important',
      webkitOverflowScrolling: 'auto !important'
    },

    '&::-webkit-scrollbar-track': {
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.3) !important',
      webkitBorderRadius: '10px !important',
      borderRadius: '10px !important'
    },

    '&::-webkit-scrollbar-thumb': {
      webkitBorderRadius: '10px !important',
      borderRadius: '10px !important',
      background: '#41617D !important',
      webkitBoxShadow: 'inset 0 0 6px rgba(0,0,0,0.5) !important'
    },

    '&::-webkit-scrollbar-thumb:window-inactive': {
      background: '#41617D !important'
    }
  },
  tableContainer: {
    margin: '16px 0',
    overflowX: 'auto', // Enables horizontal scrolling
    '& > div': {
      border: '1px solid grey',
      paddingBottom: '5px',
      [theme.breakpoints.up(1200)]: {
        minHeight: '337px'
      }
    }
  },
  select: {
    fontSize: '14px',
    marginTop: theme.spacing(4),
    cursor: 'pointer',
    border: '1px solid gray'
  },
  textField: {
    marginTop: theme.spacing(4),
    border: '1px solid gray'
  },
  textField2: {
    fontWeight: 500,
    color: '#000000'
  },
  resize: {
    fontSize: '14px'
  },
  tablecell: {
    fontSize: '13px',
    color: '#85868D',
    [theme.breakpoints.up(1200)]: {
      paddingInline: '5px'
    }
  }
})

const dateRowDefaultValues = {
  shiftstartdate: new Date(),
  shiftstarttime: '08:00',
  shiftendtime: '16:00',
  totalHour: '',
  amount: '',
  totalAmount: ''
}

const defaultValues = {
  offer_to: '',
  location: '',
  approval_deadline: new Date(),
  task_description: '',
  additional_infor: '',
  dateRows: [dateRowDefaultValues],
  salary_model: '', // Based on selected offer to contract data
  agreement_id: '', // Based on selected offer to contract data
  totalAmount: ''
}

const WorkshiftForm = ({
  classes,
  addWorkshifts,
  isEditWorkshift,
  updateWorkshift,
  // cancelEditWorkShift,
  emailSelectionList,
  allWaitingForConfirmation,
  closeWorkshiftFormDialog
}) => {
  const [selectedOfferTo, setSelectedOfferTo] = React.useState({})
  const form = useForm({
    name: 'workshift',
    onSubmit: onSubmit,
    defaultValues: defaultValues,
    validator: workShiftFormValidate,
    resetOnSubmit: true
  })
  const {
    control,
    getValues,
    clearErrors,
    formState: { errors }
  } = form
  const rowState = useFieldArray({ name: 'dateRows', control })
  const { fields } = rowState
  function onSubmit(values) {
    if (isEditWorkshift) updateWorkshift(values)
    else addWorkshifts(values, selectedOfferTo)
    closeWorkshiftFormDialog()
  }

  function removeRow(index) {
    rowState.remove(index)
  }

  const rowsData = useWatch({
    fields: ['dateRows'],
    control: control
  })

  const selectedDates = useMemo(
    () =>
      rowsData.dateRows.map((el) =>
        moment(el.shiftstartdate).format('YYYY-MM-DD')
      ),
    [rowsData.dateRows]
  )

  function addRow(value) {
    const newDate = new Date(value)
    const dateTimeRowCopy = {
      ...dateRowDefaultValues,
      shiftstartdate: newDate,
      amount:
        selectedOfferTo?.salary_model === 'Hourly rate'
          ? selectedOfferTo.amount
          : ''
    }
    rowState.append(dateTimeRowCopy)
  }

  function addExtraDateRow() {
    const currentDateRows = rowsData.dateRows
    if (!currentDateRows || currentDateRows.length === 0) {
      // Fallback if empty: just add today
      return addRow(new Date())
    }

    // Get latest date in the current date rows
    const lastDateStr = currentDateRows.reduce((latest, row) => {
      const current = new Date(row.shiftstartdate)
      return current > new Date(latest) ? current : latest
    }, currentDateRows[0].shiftstartdate)
    // Add 1 day
    // const nextDate = new Date(lastDateStr)
    // nextDate.setDate(nextDate.getDate() + 1)

    addRow(new Date(lastDateStr))
  }

  function setValue(name, value) {
    form?.setValue(name, value)
  }

  const watchFields = getWatchFields(fields, 'totalAmount')

  const disabledField = isEditWorkshift && !allWaitingForConfirmation

  React.useEffect(() => {
    const currentRows = rowsData.dateRows || []
    const sortedRows = [...currentRows].sort(
      (a, b) => new Date(a.shiftstartdate) - new Date(b.shiftstartdate)
    )

    // Check if sorting actually changed the order
    const isSameOrder = currentRows.every(
      (row, index) => row.shiftstartdate === sortedRows[index]?.shiftstartdate
    )

    if (!isSameOrder) {
      setValue('dateRows', sortedRows)
    }
  }, [rowState.dateRows, setValue])

  const handleOfferToChange = (e) => {
    const filterLightEntreList = emailSelectionList.filter(
      (item) =>
        item.offered_entrepreneur_email + '-' + item.agreement_id ===
        e.target.value
    )
    setSelectedOfferTo(filterLightEntreList[0])
  }

  // Update amount based on selected offer contract
  React.useEffect(() => {
    if (selectedOfferTo) {
      rowsData.dateRows.forEach((row, index) => {
        if (selectedOfferTo.salary_model === 'Hourly rate') {
          setValue(`dateRows.${index}.amount`, selectedOfferTo.amount)
        } else {
          setValue(`dateRows.${index}.amount`, '')
          setValue('totalAmount', '')
        }
      })
    }
  }, [selectedOfferTo])

  return (
    <Translation>
      {(t) => (
        <Form {...form}>
          <div style={{ padding: '15px' }} className={classes.root}>
            <div className="row">
              <div className="dashboard-content-header">
                <h1>
                  {isEditWorkshift
                    ? t('employer.workshifts.h1d')
                    : t('employer.workshifts.h1c')}
                </h1>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12 col-sm-12 col-md-6 col-lg-6">
                <LightEntreSelectField
                  classes={classes}
                  lightEntreList={emailSelectionList}
                  control={control}
                  disabled={disabledField}
                  onChange={handleOfferToChange}
                />
              </div>
              <div className="col-xs-12 col-sm-12 col-md-3 col-lg-3">
                <Field
                  name="location"
                  label={t('employer.workshifts.fld2')}
                  className={classNames(classes.textField, classes.resize)}
                  inputType="autocomplete-places"
                  control={control}
                  labelStyle={{ marginTop: '24px' }}
                  disabled={disabledField}
                />
              </div>
              <div className="col-xs-12 col-sm-12 col-md-3 col-lg-3">
                <Field
                  name="approval_deadline"
                  label={t('employer.workshifts.fld3')}
                  className={classNames(classes.textField, classes.resize)}
                  inputType="datepicker"
                  control={control}
                  minDate={new Date()}
                  disabled={disabledField}
                  labelStyle={{
                    marginTop: 3,
                    fontSize: '14px'
                  }}
                />
                <p
                  style={{
                    fontSize: '13px',
                    padding: '10px 0px 0px',
                    marginBottom: 0
                  }}
                >
                  {t('employer.workshifts.note1')}
                </p>
              </div>
            </div>
            <div className="row">
              <div className="col-xs-12 col-sm-12 col-md-12 col-lg-12">
                <Field
                  name="task_description"
                  label={t('employer.workshifts.fld4')}
                  className={classNames(classes.textField, classes.resize)}
                  inputType="textfield"
                  control={control}
                  disabled={disabledField}
                />
              </div>
            </div>
            <div className="row">
              <div
                className="col-xs-12 col-sm-12 col-md-12 col-lg-12"
                style={{
                  marginTop: '16px',
                  color: errors?.additional_infor ? 'red' : '#0000008A',
                  fontWeight: 500
                }}
              >
                <span>{t('employer.workshifts.fld5')}</span>
                <div
                  style={{
                    border: errors?.additional_infor ? '1px solid red' : '',
                    marginTop: '5px'
                  }}
                >
                  <Controller
                    render={({ field: { value, onChange } }) => {
                      const handleEditorChange = (editorValue) => {
                        onChange(editorValue)
                      }
                      return (
                        <RichTextEditorComponent
                          value={value}
                          onEditorChange={handleEditorChange}
                          height="200px"
                          disabled={disabledField}
                        />
                      )
                    }}
                    name="additional_infor"
                    control={control}
                  />
                </div>
              </div>
            </div>
            <div className="row">
              <div
                className="col-xs-12 col-sm-12 col-md-12 col-lg-3"
                style={{
                  marginTop: '16px',
                  padding: '0px 15px'
                }}
              >
                <div
                  style={{
                    border: '1px solid grey',
                    padding: 0,
                    display: 'inline-block'
                  }}
                >
                  <BasicDateCalendar
                    disabledDates={[]}
                    onChange={addRow}
                    disabled={isEditWorkshift ? true : false}
                    disablePast={true}
                    selectedDates={selectedDates}
                  />
                </div>
                <div>
                  <span
                    style={{
                      color: '#F67131',
                      fontSize: '13px',
                      fontWeight: 500
                    }}
                  >
                    <i>{t('employer.workshifts.fld10')}</i>
                  </span>
                </div>
              </div>
              <div
                className={
                  classes.tableContainer +
                  ' col-xs-12 col-sm-12 col-md-12 col-lg-9'
                }
              >
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100%' // or a fixed height like '600px' if needed
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell
                          className={classes.tablecell}
                          style={{ width: '100px' }}
                        >
                          {t('employer.workshifts.fld6')}
                        </TableCell>
                        <TableCell className={classes.tablecell}>
                          {t('employer.workshifts.fld7')}
                        </TableCell>
                        <TableCell className={classes.tablecell}>
                          {t('employer.workshifts.fld8')}
                        </TableCell>
                        <TableCell className={classes.tablecell}>
                          {t('employer.workshifts.fld11')}
                        </TableCell>
                        <TableCell className={classes.tablecell}>
                          {t('employer.workshifts.fld9')}
                        </TableCell>
                        {selectedOfferTo?.salary_model === 'Commission' ||
                        (isEditWorkshift &&
                          form.getValues('salary_model') === 'Commission') ? (
                          <TableCell className={classes.tablecell}>
                            {t('employer.workshifts.fld12')}
                          </TableCell>
                        ) : null}
                        <TableCell className={classes.tablecell}></TableCell>
                        {isEditWorkshift ? (
                          <TableCell className={classes.tablecell}>
                            {t('employer.workshifts.col8')}
                          </TableCell>
                        ) : (
                          <TableCell className={classes.tablecell}></TableCell>
                        )}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getShiftDateRow(
                        fields,
                        removeRow,
                        control,
                        t,
                        isEditWorkshift,
                        setValue,
                        selectedOfferTo,
                        getValues,
                        rowsData.dateRows,
                        clearErrors
                      )}
                      <TableRow>
                        <TableCell
                          className={classes.tablecell}
                          colSpan={8}
                          style={{ borderBottom: 'none', padding: 0 }}
                        >
                          <div className="pull-right">
                            <IconButton
                              component="span"
                              size="medium"
                              style={{
                                color: '#F67131'
                              }}
                              onClick={addExtraDateRow}
                            >
                              <AddCircle fontSize="large" />
                            </IconButton>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                  <div style={{ marginTop: 'auto', paddingTop: '16px' }}>
                    <hr style={{ margin: 0 }} />
                    <div
                      style={{ display: 'flex', justifyContent: 'flex-end' }}
                    >
                      <div
                        style={{
                          padding: '8px',
                          fontWeight: 500
                        }}
                      >
                        {t('employer.workshifts.totalAmount')}
                      </div>
                      <div style={{ width: '140px' }}>
                        <WatchField
                          fields={watchFields.totalSum}
                          control={control}
                        >
                          {(fields) => (
                            <SumField
                              name="totalAmount"
                              control={control}
                              setValue={setValue}
                              watchValue={calculateSum(fields)}
                              className={classNames(
                                classes.textField2,
                                classes.resize
                              )}
                            />
                          )}
                        </WatchField>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="pull-right" style={{ paddingBottom: '15px' }}>
              {isEditWorkshift ? (
                <ul className="nav nav-pills pull-right">
                  <li>
                    {/* <Button
                      variant="outlined"
                      onClick={() => {
                        cancelEditWorkShift()
                        form.reset()
                        closeWorkshiftFormDialog()
                      }}
                    >
                      {t('employer.workshifts.btn2')}
                    </Button> */}
                  </li>
                  <li>
                    <Button variant="contained" type="submit">
                      {t('employer.workshifts.btn6')}
                    </Button>
                  </li>
                </ul>
              ) : (
                <Button variant="contained" type="submit">
                  {t('employer.workshifts.btn3')}
                </Button>
              )}
            </div>
          </div>
        </Form>
      )}
    </Translation>
  )
}

const getWatchFields = (fields, totalField) => {
  const totalSum = []
  for (let i = 0; i < fields.length; i++) {
    totalSum.push(`dateRows.${i}.${totalField}`)
  }
  return { totalSum }
}

const getShiftDateRow = (
  fields,
  remove,
  control,
  t,
  isEditWorkshift,
  setValue,
  selectedOfferTo,
  getValues,
  watchRowsData,
  clearErrors
) =>
  fields.map((field, index) => (
    <ShiftDateRowComponent
      index={index}
      key={field.id}
      date={`dateRows.${index}.shiftstartdate`}
      work_starts={`dateRows.${index}.shiftstarttime`}
      work_ends={`dateRows.${index}.shiftendtime`}
      totalHour={`dateRows.${index}.totalHour`}
      // hourly_rate={`dateRows.${index}.hourly_rate`}
      amount={`dateRows.${index}.amount`}
      totalAmount={`dateRows.${index}.totalAmount`}
      remove={remove}
      control={control}
      t={t}
      isEditWorkshift={isEditWorkshift}
      status_entrepreneur={field.workshift_status_entrepreneur}
      setValue={setValue}
      selectedOfferTo={selectedOfferTo}
      getValues={getValues}
      watchRowsData={watchRowsData}
      clearErrors={clearErrors}
    />
  ))

export default withStyles(styles)(WorkshiftForm)
